import React from 'react'
import { NextPage } from 'next'
import { SingleItemResponse } from '@lib/kontent'
import { ToursPage } from '@lib/kontent/models/ToursPage'
import { Box, Grid, Container, Heading, Text } from '@components/gassan-ui'
import { pickSeoProps } from '@components/Page/Page'
import { Footer, Header, Page, PageTransition } from '@components/Page'
import FeaturedTour from '@components/_tours/FeaturedTour'
import Usps from '@components/_tours/Usps'
import { rem } from 'polished'
import TourArrangementCard from '@components/_tours/TourArrangementCard'
import TripAdvisor from '@components/_tours/TripAdvisor'
import { useLocalization } from '@components/Localization'

type Props = SingleItemResponse<ToursPage>

const Tours: NextPage<Props> = ({ item, modular_content }) => {
  const { lang } = useLocalization()
  const firstFeatured = modular_content[item.elements.first_featured_tour.value[0]]
  const secondFeatured = modular_content[item.elements.second_featured_tour.value[0]]
  const tours = item.elements.other_tours.value.map(tour => modular_content[tour])
  const arrangements = item.elements.arrangement_items.value.map(
    arrangement => modular_content[arrangement]
  )

  return (
    <Page {...pickSeoProps(item.elements)}>
      <Header initialVariant="absolute" />
      <PageTransition>
        <Container maxWidth={rem(1450)}>
          <Box
            maxWidth={rem(840)}
            pt={{ _: '1.5rem', small: '3rem', large: '5rem', xlarge: '7rem' }}
            pb={{ _: '2.5rem', large: '4rem', xlarge: '5rem' }}
          >
            <Heading variant="h1" as="h1" mb={['1rem', '1.5rem']}>
              {item.elements.title.value}
            </Heading>
            <Text mb={0}>{item.elements.description.value}</Text>
          </Box>
        </Container>
        <FeaturedTour variant="text-video" item={firstFeatured.elements} />
        <Usps />
        <FeaturedTour variant="video-text" item={secondFeatured.elements} />
        <Container
          display="grid"
          maxWidth={rem(1450)}
          mt={{ _: '1.25rem', small: '3rem', xlarge: '5rem' }}
          gridTemplateColumns={{ _: '1fr', medium: '1fr 1fr', large: 'repeat(3, 1fr)' }}
          gridGap={{ _: '1.25rem', small: '3rem', medium: '1.25rem', large: '1.25rem' }}
        >
          {tours.map((tour, index) => (
            <TourArrangementCard item={tour.elements} variant="tour" key={index} />
          ))}
        </Container>
        <Container
          maxWidth={rem(1450)}
          mt={{ _: '3rem', small: '6.5rem', large: '5.5rem', xlarge: '7.5rem' }}
        >
          <Heading
            variant="h2"
            as="h2"
            textAlign={{ _: 'center', large: 'left' }}
            mb={{ _: '1.5rem', small: '3rem', large: '2.5rem', xlarge: '3.5rem' }}
          >
            {item.elements.arrangement_title.value}
          </Heading>
          {arrangements.length === 0 && (
            <Text mt={{ _: '0rem', small: '-2rem' }} textAlign={{ _: 'center', large: 'left' }}>
              {lang === 'nl'
                ? 'Momenteel geen arrangementen beschikbaar'
                : 'Currently no arrangements available'}
            </Text>
          )}
          <Grid
            gridTemplateColumns={{ _: '1fr', medium: '1fr 1fr', large: 'repeat(3, 1fr)' }}
            gridGap={{ _: '1.25rem', small: '3rem', medium: '1.25rem', large: '1.25rem' }}
          >
            {arrangements.map((arrangement, index) => (
              <TourArrangementCard item={arrangement.elements} variant="arrangement" key={index} />
            ))}
          </Grid>
        </Container>
        <TripAdvisor />
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default Tours
